import React from 'react';

import { Icon } from 'ui';

import './_stat-card.scss';

type StatCardIconVariant = 'group' | 'mastery' | 'rollback' | 'success' | 'time' | 'timeline';

type StatCardLayout = 'inline' | 'spread' | 'stacked';

type StatCardProps = {
  ariaLabel?: string;
  children?: React.ReactNode;
  description?: React.ReactNode;
  iconType: string;
  iconVariant: StatCardIconVariant;
  isExpandable?: boolean;
  isExpanded?: boolean;
  label: string;
  layout?: StatCardLayout;
  onToggle?: () => void;
  value: React.ReactNode;
};

/**
 * StatCard - Reusable stat card displaying an icon, a value and a label.
 * - When `isExpandable`, a toggle button reveals `children` below.
 * - When NOT expandable, `children` is always rendered below.
 * - `description` is always rendered below the row when provided.
 */
function StatCard({
  ariaLabel,
  children,
  description,
  iconType,
  iconVariant,
  isExpandable = false,
  isExpanded = false,
  label,
  layout = 'inline',
  onToggle,
  value,
}: Readonly<StatCardProps>): JSX.Element {
  const hasBody = description || children;
  const cardClassName = `stat-card stat-card--layout-${layout}${isExpandable || hasBody ? ' stat-card--expandable' : ''}`;
  const rowClassName = `stat-card__row${isExpandable ? ' stat-card__row--clickable' : ''}`;
  const toggleClassName = `stat-card__toggle${isExpanded ? ' stat-card__toggle--expanded' : ''}`;
  const shouldRenderChildren = children && (!isExpandable || isExpanded);

  function renderRowContent(): JSX.Element {
    return (
      <>
        <div className={`stat-card__icon stat-card__icon--${iconVariant}`}>
          <Icon size="default" name={iconType} />
        </div>
        <div className="stat-card__info">
          <span className="stat-card__label">{label}</span>
          <span className="stat-card__value">{value}</span>
        </div>
        {isExpandable && (
          <span aria-hidden="true" className={toggleClassName}>
            <Icon color="grey-03" size="default" name="arrow-down" />
          </span>
        )}
      </>
    );
  }

  return (
    <div className={cardClassName}>
      {isExpandable ? (
        <button aria-expanded={isExpanded} aria-label={ariaLabel ?? label} className={rowClassName} onClick={onToggle} type="button">
          {renderRowContent()}
        </button>
      ) : (
        <div className={rowClassName}>{renderRowContent()}</div>
      )}
      {description && <p className="stat-card__description">{description}</p>}
      {shouldRenderChildren && children}
    </div>
  );
}

export default StatCard;
